import React from "react";

import {
    BILLING_DISPLAY_FORMAT,
    roundUp,
    useEventDataOfSelectedEvent,
    SELECTORS,
} from "@Utils";
import { isInstallmentPaymentType } from "repoV2/utils/common/listing";

import { IBillRowData } from "@Modules/common/BillRows/IBillRows";
import { getIsTicketingEnabled } from "@Modules/eventPage/utils/getIsTicketingEnabled";

import { useSelector } from "react-redux";
import { useEvaluatePaymentPrice } from "@Modules/eventPage/utils/useEvaluatePaymentPrice";
import {
    PAYMENT_DEBIT_MODES,
    PAYMENT_DEBIT_MODES_LABEL,
} from "repoV2/features/Payments/Payments.constants";
import { isAutoDebitActiveOnMainListingData } from "repoV2/features/Listing/Listing.utils";
import { IPaymentSummary } from "../IPaymentSummary";

import Currency from "../components/CurrencySymbol";
import { useInstalmentsDetailsContext } from "../../PaymentPlan/utils/InstalmentsDetailsContext";

import styles from "../paymentSummary.module.scss";

const useExpandedBillRowData = ({
    isRebooking,
    numberOfCustomers,
    basePaymentValues,
    paymentInfos,
    eventType,
    earlyBirdObj,
    eventCurrencySymbol,
    pricePerHead,
    eventTitle,
    otherListings,
    additionalListings,
    billingData,
    isInternationalCreator,
    shippingText,
    toggleIsPaymentSummaryExpanded,
    multipleOfferingsSelected,
    isPaymentSummaryExpanded,
    isGSTRegistered,
}: IPaymentSummary.IExpandedBillProps) => {
    const { event, eventData } = useEventDataOfSelectedEvent();
    const hostData = useSelector(SELECTORS.hostData);
    const { selectedPaymentType } = useInstalmentsDetailsContext();
    const isInstallmentSelected = isInstallmentPaymentType(selectedPaymentType);
    const isAutoDebitActiveOnMainListing =
        isAutoDebitActiveOnMainListingData(eventData);

    const {
        // Boolean flags for certain conditions
        flags: {
            enableGST, // Whether to include GST? (Prerequisite is isMerchandise === true)
            isMerchandise, // Is this listing a merchandise type
            // showAllForCGst,
            billingDisplayFormatState,
            hasPincode,
            shiprocketEnabled,
        },
        // Percentage values
        percentages: { gst },
        // Absolute numbers for price values
        priceValues: {
            unitPrice, // Base unit price
            cgstExclusiveUnitPrice,
            cgstExclusiveShippingPrice,
            creatorGstPrice,
            shippingPrice = 0,
        },
        // Scaling factors
        factors: {
            billingMultiplier: numberOfItems, // Number of instances of items being bought for this SKU
        },
    } = basePaymentValues;

    const evaluatePaymentPriceProps = {
        numberOfItems,
        eventType,
        earlyBirdObj,
        cgstExclusiveUnitPrice,
        pricePerHead,
        multipleOfferingsSelected,
        unitPrice,
        billingDisplayFormatState,
    };

    const evaluatePricePerNumberOfItems = useEvaluatePaymentPrice(
        evaluatePaymentPriceProps
    );

    const hideShippingCharge = !(
        isMerchandise &&
        // show shipping charge user entered pincode if shiprocket enabled
        (hasPincode || !shiprocketEnabled)
    );

    const commonBillRowsDatas = {
        customerCount: {
            renderLeft: "Customer count",
            renderRight: `x ${numberOfCustomers}`,
            hide: !getIsTicketingEnabled({ event, eventData, isRebooking }),
        },
        //  // If multiple listings are selected, this below will render the individual price and quantity of them all
        paymentInfosWhenMultipleOfferingsSelected: paymentInfos.reduce(
            (rows, item) => {
                if (item.uuid === basePaymentValues.uuid) {
                    // for main offering
                    const mainOfferingShippingCharge =
                        billingDisplayFormatState ===
                        BILLING_DISPLAY_FORMAT.DEFAULT_SHOW_ALL
                            ? shippingPrice
                            : cgstExclusiveShippingPrice;
                    return [
                        ...rows,
                        {
                            renderLeft: `${numberOfItems} x ${eventTitle}`,
                            renderRight:
                                earlyBirdObj && !isInstallmentSelected ? (
                                    <>
                                        <span>
                                            <Currency
                                                currency={eventCurrencySymbol}
                                            />
                                            {roundUp(unitPrice * numberOfItems)}
                                        </span>
                                        &nbsp;
                                        <del>
                                            <Currency
                                                currency={eventCurrencySymbol}
                                            />
                                            {roundUp(
                                                pricePerHead * numberOfItems
                                            )}
                                        </del>
                                    </>
                                ) : (
                                    <span>
                                        <Currency
                                            currency={eventCurrencySymbol}
                                        />
                                        {roundUp(pricePerHead * numberOfItems)}
                                    </span>
                                ),
                            hide: !multipleOfferingsSelected,
                        },
                        {
                            options: { coupleWithPreviousRow: true },
                            hide:
                                hideShippingCharge ||
                                !multipleOfferingsSelected,
                            renderLeft: "Shipping Charges",
                            renderRight: (
                                <>
                                    <Currency currency={eventCurrencySymbol} />
                                    {roundUp(mainOfferingShippingCharge || 0)}
                                </>
                            ),
                        },
                    ];
                }
                // for upsell offering
                const updatedRows = [
                    ...rows,
                    {
                        renderLeft: `${
                            otherListings[item.uuid].quantity ?? 1
                        } x ${
                            (additionalListings || [])?.filter(
                                a => a.additional_listing_uid === item.uuid
                            )?.[0]?.title
                        }`,
                        renderRight: (
                            <>
                                <Currency currency={eventCurrencySymbol} />
                                &nbsp;
                                {roundUp(
                                    item.priceValues.unitPrice *
                                        item.factors.billingMultiplier
                                )}
                            </>
                        ),
                        hide: !multipleOfferingsSelected,
                    },
                ];

                const {
                    flags: {
                        isMerchandise: isUpsellMerchandise,
                        shiprocketEnabled: isUpsellShiprocketEnabled,
                    },
                    priceValues: {
                        shippingPrice: upsellShippingPrice,
                        cgstExclusiveShippingPrice:
                            upsellCgstExclusiveShippingPrice,
                    },
                } = item;
                const hideUpsellShippingCharge = !(
                    isUpsellMerchandise && !isUpsellShiprocketEnabled
                );

                if (isUpsellMerchandise && !hideUpsellShippingCharge) {
                    const upsellShippingCharge =
                        billingDisplayFormatState ===
                        BILLING_DISPLAY_FORMAT.DEFAULT_SHOW_ALL
                            ? upsellShippingPrice
                            : upsellCgstExclusiveShippingPrice;
                    updatedRows.push({
                        options: {
                            coupleWithPreviousRow: true,
                        },
                        hide: hideUpsellShippingCharge,
                        renderLeft: "Shipping Charges",
                        renderRight: (
                            <>
                                <Currency currency={eventCurrencySymbol} />
                                {roundUp(upsellShippingCharge || 0)}
                            </>
                        ),
                    });
                }

                return updatedRows;
            },
            [] as IBillRowData[]
        ),

        creatorTaxIndicatorWhenMultipleOfferingsSelected: {
            renderLeft: "(Inclusive of taxes)",
            hide: !multipleOfferingsSelected,
        },
        discount: {
            hide: billingData.discountPrice <= 0,
            renderLeft: "Discount",
            renderRight: (
                <>
                    -
                    <span>
                        <Currency currency={eventCurrencySymbol} />
                        &nbsp;
                        {roundUp(
                            Math.min(
                                billingData.subtotal * numberOfCustomers,
                                billingData.discountPriceWScalingFactor
                            )
                        )}
                    </span>
                </>
            ),
        },
        totalPayableAmount: {
            renderLeft: "Total Payable Amount",
            renderRight: (
                <>
                    <Currency currency={eventCurrencySymbol} />
                    &nbsp;
                    {roundUp(billingData.totalPrice)}
                </>
            ),
            options: {
                bold: true,
            },
        },
    };

    const commonBillRowsForSeparateCgstAndPretendCgstInapplicable = [
        commonBillRowsDatas.customerCount,
        evaluatePricePerNumberOfItems,
        ...commonBillRowsDatas.paymentInfosWhenMultipleOfferingsSelected,
        commonBillRowsDatas.creatorTaxIndicatorWhenMultipleOfferingsSelected,
        commonBillRowsDatas.discount,
        {
            hide: hideShippingCharge || multipleOfferingsSelected,
            renderLeft: "Shipping Charges",
            renderRight: (
                <>
                    <Currency currency={eventCurrencySymbol} />
                    {roundUp(cgstExclusiveShippingPrice || 0)}
                </>
            ),
        },
        {
            hide: !(enableGST && billingData.internetHandlingFees > 0),
            renderLeft: "Internet handling fees",
            renderRight: (
                <>
                    <Currency currency={eventCurrencySymbol} />
                    &nbsp;
                    {roundUp(billingData.internetHandlingFees)}
                </>
            ),
        },
    ];

    let billRowsData: IBillRowData[] = [];
    switch (billingDisplayFormatState) {
        case BILLING_DISPLAY_FORMAT.DEFAULT_SHOW_ALL:
            billRowsData = [
                commonBillRowsDatas.customerCount,
                evaluatePricePerNumberOfItems,
                ...commonBillRowsDatas.paymentInfosWhenMultipleOfferingsSelected,
                commonBillRowsDatas.creatorTaxIndicatorWhenMultipleOfferingsSelected,
                commonBillRowsDatas.discount,
                {
                    hide: hideShippingCharge || multipleOfferingsSelected,
                    renderLeft: "Shipping Charges",
                    renderRight: (
                        <>
                            <Currency currency={eventCurrencySymbol} />
                            {roundUp(shippingPrice || 0)}
                        </>
                    ),
                },
                {
                    hide: !(
                        enableGST &&
                        billingData.internetHandlingFees +
                            billingData.gstPrice >
                            0
                    ),
                    renderLeft: "Internet handling fees",
                    renderRight: (
                        <>
                            <Currency currency={eventCurrencySymbol} />
                            &nbsp;
                            {roundUp(
                                billingData.internetHandlingFees +
                                    billingData.gstPrice
                            )}
                        </>
                    ),
                },
                {
                    renderLeft: "Payment plan",
                    renderRight: (
                        <span className={styles.successStyle}>
                            {
                                PAYMENT_DEBIT_MODES_LABEL[
                                    PAYMENT_DEBIT_MODES.AUTO_DEBIT
                                ]
                            }
                        </span>
                    ),
                    hide: !isAutoDebitActiveOnMainListing,
                },

                { rowType: "divider" },
                commonBillRowsDatas.totalPayableAmount,
            ];
            break;

        case BILLING_DISPLAY_FORMAT.SEPARATE_CGST:
            billRowsData = [
                ...commonBillRowsForSeparateCgstAndPretendCgstInapplicable,
                {
                    hide: !(
                        enableGST && billingData.gstPrice + creatorGstPrice > 0
                    ),
                    renderLeft: `${
                        hostData?.event_payment_summary_tax_label ||
                        (isGSTRegistered ? "GST" : "Tax")
                    }`,
                    renderRight: (
                        <>
                            <Currency currency={eventCurrencySymbol} />
                            &nbsp;
                            {roundUp(billingData.gstPrice + creatorGstPrice)}
                        </>
                    ),
                },
                { rowType: "divider" },
                commonBillRowsDatas.totalPayableAmount,
            ];
            break;

        case BILLING_DISPLAY_FORMAT.PRETEND_CGST_INAPPLICABLE:
            billRowsData = [
                ...commonBillRowsForSeparateCgstAndPretendCgstInapplicable,
                {
                    hide: !(enableGST && billingData.gstPrice > 0),
                    renderLeft: `${
                        hostData?.event_payment_summary_tax_label ||
                        (isInternationalCreator ? "Tax" : "GST")
                    } (@${gst}%)`,
                    renderRight: (
                        <>
                            <Currency currency={eventCurrencySymbol} />
                            &nbsp;
                            {roundUp(billingData.gstPrice)}
                        </>
                    ),
                },
                { rowType: "divider" },
                commonBillRowsDatas.totalPayableAmount,
            ];
            break;

        default:
            break;
    }

    const _shippingText =
        shippingText ?? "* Shipping details will be collected in the next step";

    return {
        billRowsData,
        isMerchandise,
        shippingText: _shippingText,
        toggleIsPaymentSummaryExpanded,
        isPaymentSummaryExpanded,
    };
};
export default useExpandedBillRowData;
